import { computed, inject, Injectable, signal } from '@angular/core';
import Keycloak, {
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakServerConfig,
} from 'keycloak-js';

import { Store } from '@ngrx/store';

import {
  getJwtExpiration,
  getSsoBrandingParams,
  getTheme,
  RequestLogout,
} from '@resident-nx/shared';
import { LocalStorageService } from '../storage/local-storage.service';

export const KeycloakTokenStorageKey = {
  TOKEN: 'kc-token',
  REFRESH_TOKEN: 'kc-refresh-token',
  ID_TOKEN: 'kc-id-token',
  IMPERSONATION_TOKEN: 'kc-impersonation-token',
  IMPERSONATION_REFRESH_TOKEN: 'kc-impersonation-refresh-token',
} as const;

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  #keycloak?: Keycloak;
  readonly #storage = inject(LocalStorageService);
  readonly #store = inject(Store);
  readonly #theme = this.#store.selectSignal(getTheme);
  readonly tokenSignal = signal('');
  // Whether the current session was started via impersonation (see
  // ImpersonateComponent). Consumers use this to put the app into read-only
  // mode. Set on every impersonation entry, including the restore-on-reload
  // path in initializeKeycloak().
  readonly isImpersonating = signal(false);
  // Expiry of the current access token, used to count down the remaining
  // impersonation session time. Null while unauthenticated or when the token
  // carries no exp claim.
  readonly tokenExpiresAt = computed(() => getJwtExpiration(this.tokenSignal()));

  public get refreshToken() {
    return this.#keycloak?.refreshToken;
  }

  public get token() {
    return this.#keycloak?.token;
  }

  public get sessionId() {
    return this.#keycloak?.sessionId;
  }

  public get instance(): Readonly<Keycloak> | undefined {
    return this.#keycloak;
  }

  async init(config: KeycloakServerConfig, initData: KeycloakInitOptions = {}): Promise<boolean> {
    this.#keycloak = new Keycloak(config);

    // Persist tokens across page reloads so the session can be restored without
    // relying on the silent check-sso iframe — which depends on the Keycloak
    // session cookie being readable cross-site. On white-label custom domains
    // (e.g. gwh.de) that cookie is third-party and blocked, so the persisted
    // refresh token is the only thing that survives a reload. See
    // initializeKeycloak(), which feeds these back into init().
    this.#keycloak.onAuthSuccess = () => {
      this.tokenSignal.set(this.#keycloak.token);
      this.#persistTokens();
    };
    this.#keycloak.onAuthRefreshSuccess = () => {
      this.tokenSignal.set(this.#keycloak.token);
      this.#persistTokens();
    };
    this.#keycloak.onAuthLogout = () => {
      this.tokenSignal.set('');
      this.#clearTokens();
    };
    this.#keycloak.onAuthRefreshError = () => {
      this.tokenSignal.set('');
      this.#clearTokens();
      this.#store.dispatch(RequestLogout({}));
    };
    // Refresh proactively when the access token expires, regardless of in-app
    // HTTP activity. The auth interceptor only refreshes on app request
    // responses; long-lived embeds (e.g. the IDA chatbot) talk to their own
    // API and fire no app requests, so without this the token silently expires
    // and their calls start failing. onAuthRefreshSuccess updates tokenSignal.
    this.#keycloak.onTokenExpired = () => {
      this.updateToken().catch(err => console.error('Token refresh on expiry failed', err));
    };

    try {
      return await this.#keycloak.init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
        silentCheckSsoRedirectUri: window.location.origin + '/assets/html/silent-check-sso.html',
        ...initData,
      });
    } catch (error) {
      console.error('Keycloak init failed', error);
      return false;
    }
  }

  initImpersonation(config: KeycloakServerConfig, token: string, refreshToken?: string): void {
    this.#keycloak = new Keycloak(config);
    this.#keycloak.token = token;
    this.#keycloak.authenticated = true;
    this.tokenSignal.set(token);
    this.isImpersonating.set(true);
    this.#storage.setItem(KeycloakTokenStorageKey.IMPERSONATION_TOKEN, token);
    this.#storage.setItem(KeycloakTokenStorageKey.IMPERSONATION_REFRESH_TOKEN, refreshToken);
  }

  async login(options: KeycloakLoginOptions) {
    if (!this.#keycloak) {
      return;
    }
    try {
      // keycloak-js only forwards a fixed allow-list of options to the login
      // URL, so custom branding params can't be passed through login(). Build
      // the URL ourselves (createLoginUrl still sets up state/nonce/PKCE),
      // append the white-label theme colors, then redirect. The Keycloak login
      // theme reads these params to brand the hosted login page.
      const loginUrl = new URL(await this.#keycloak.createLoginUrl(options));
      this.#appendBrandingParams(loginUrl.searchParams);
      window.location.assign(loginUrl.toString());
    } catch (e) {
      console.error('Keycloak login failed', e);
    }
  }

  // The theme is loaded pre-auth (getAppSettings uses SKIP_AUTH), so it is
  // normally available here. Shared theme→param contract with the mobile
  // AuthNSService (see ART-19562).
  #appendBrandingParams(searchParams: URLSearchParams): void {
    const params = getSsoBrandingParams(this.#theme());
    for (const [key, value] of Object.entries(params)) {
      searchParams.set(key, value);
    }
  }

  async logout(redirectUri?: string) {
    const wasImpersonating = this.isImpersonating();
    this.#clearTokens();

    // An impersonation session has no Keycloak session behind it, so calling
    // the logout endpoint would only fail (or bounce us through SSO). Drop the
    // tokens and reload into the unauthenticated app instead.
    if (wasImpersonating) {
      this.#keycloak = undefined;
      window.location.href = window.location.origin;
      return;
    }

    try {
      await this.#keycloak?.logout({
        redirectUri,
      });
    } catch (e) {
      console.error('Keycloak logout failed', e);
    }
  }

  updateToken(minValidity = 5): Promise<boolean> {
    return this.#keycloak.updateToken(minValidity);
  }

  #persistTokens(): void {
    const { token, refreshToken, idToken } = this.#keycloak ?? {};
    if (token) this.#storage.setItem(KeycloakTokenStorageKey.TOKEN, token);
    if (refreshToken) this.#storage.setItem(KeycloakTokenStorageKey.REFRESH_TOKEN, refreshToken);
    if (idToken) this.#storage.setItem(KeycloakTokenStorageKey.ID_TOKEN, idToken);
  }

  #clearTokens(): void {
    this.#storage.removeItem(KeycloakTokenStorageKey.TOKEN);
    this.#storage.removeItem(KeycloakTokenStorageKey.REFRESH_TOKEN);
    this.#storage.removeItem(KeycloakTokenStorageKey.ID_TOKEN);
    this.#storage.removeItem(KeycloakTokenStorageKey.IMPERSONATION_TOKEN);
    this.#storage.removeItem(KeycloakTokenStorageKey.IMPERSONATION_REFRESH_TOKEN);
    this.isImpersonating.set(false);
  }
}
